import React from 'react';
import _ from 'lodash';

import {
  Button,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography
} from '@mui/material';
import {
  Done,
  Error
} from '@mui/icons-material';
import {
  useFieldArray,
  useForm
} from 'react-hook-form';
import {
  Form,
  fields,
  formUtil
} from 'iron-forge-react-modules';
import {
  BaseWidget,
  activityTemplates,
  routeDirectory
} from 'modules';
import {
  useNavigate,
  useParams
} from 'react-router-dom';

import { Validator } from 'jsonschema';
import * as adminShared from '../shared/validationErrors';
import * as dataService from './dataService';



const ControlledTextField = fields.TextField.genControllerComponent<IFormState>();

export interface IFormState {
  errors: IValidationError[];
}



export interface IValidationError {
  type: 'server' | 'data' | 'parse';
  property: string;
  message: string;
  newValue: string | number;
}



export const ValidateOrderActivity: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{
    storeId: string;
    externalOrderId: string;
  }>();
  const queryTuple = dataService.useQuery(params.externalOrderId ?? '');
  const [
    validateCachedOrder,
    validateCachedOrderTuple
  ] = dataService.useValidateCachedOrder(params.externalOrderId ?? '');
  const [
    overrideCachedOrder,
    overrideCachedOrderTuple
  ] = dataService.useOverrideCachedOrder(params.externalOrderId ?? '');

  const [orderAsString, setOrderAsString] = React.useState('');
  const [validationErrors, setValidationErrors] = React.useState<IValidationError[]>([]);
  const [didValidateWithServer, setDidValidateWithServer] = React.useState(false);
  const form = useForm<IFormState>({
    defaultValues: {
      errors: []
    }
  });
  const fieldArray = useFieldArray<IFormState>({
    control: form.control,
    name: 'errors'
  });



  React.useEffect(
    () => {
      const errors = _.filter(validationErrors, err => err.type === 'data');
      formUtil.setFieldsFromQuery(
        { errors },
        form.setValue,
        ['errors']
      );
    },
    [
      form.setValue,
      validationErrors
    ]
  );

  React.useEffect(
    () => {
      setDidValidateWithServer(false);
    },
    [orderAsString]
  );



  React.useEffect(
    () => {
      if (!_.isEmpty(queryTuple.data.originalOrder) && !orderAsString) {
        setOrderAsString(JSON.stringify(queryTuple.data?.originalOrder ?? {}, null, 2));
      }
    },
    [
      orderAsString,
      queryTuple.data.originalOrder
    ]
  );



  React.useEffect(
    () => {
      try {
        const order = JSON.parse(orderAsString);
        const v = new Validator();
        const result = v.validate(order, queryTuple.data.salesOrderSchema, {
          nestedErrors: true
        } as unknown as undefined);
        const flattenedErrors = adminShared.flattenValidationErrors(result.errors);
        setValidationErrors(_.map(flattenedErrors, (err): IValidationError => ({
          property: adminShared.getValidationErrorPropertyPath(err),
          message: err.toString(),
          type: 'data',
          newValue: ''
        })));

      } catch (err) {
        setValidationErrors([{
          type: 'parse',
          message: "Invalid JSON",
          property: '',
          newValue: ''
        }]);
      }
    },
    [
      orderAsString,
      queryTuple.data.salesOrderSchema
    ]
  );



  const validateOrder = React.useCallback(
    async (orderAsString: string) => {
      if (!didValidateWithServer && orderAsString) {
        const results = await validateCachedOrder(JSON.parse(orderAsString));
        setValidationErrors(_.map(results, (res): IValidationError => ({
          type: 'server',
          message: res,
          property: '',
          newValue: ''
        })));
        setDidValidateWithServer(true);
      }
    },
    [
      didValidateWithServer,
      validateCachedOrder
    ]
  );



  React.useEffect(
    () => {
      if (_.isEmpty(validationErrors) && !validateCachedOrderTuple.loading && !didValidateWithServer && orderAsString) {
        validateOrder(orderAsString);
      }
    },
    [
      didValidateWithServer,
      validateCachedOrderTuple,
      validationErrors,
      orderAsString,
      validateOrder
    ]
  );

  const handleClickOverrideOrderForProcessing = React.useCallback(
    async () => {
      await overrideCachedOrder(JSON.parse(orderAsString));
      navigate(routeDirectory.admin.dataCache.path('invalid', params?.storeId));
    },
    [
      navigate,
      overrideCachedOrder,
      orderAsString,
      params?.storeId
    ]
  );



  const processSubmit = React.useCallback(
    (formState: IFormState) => {
      const order = _.cloneDeep(JSON.parse(orderAsString) as object);
      _.forEach(formState.errors, err => {
        _.set(order, err.property, err.newValue);
      });
      setOrderAsString(JSON.stringify(order, null, 2));
    },
    [orderAsString]
  );



  return (
    <activityTemplates.AdminActivityTemplate.FC>
      <Grid
        container
        rowSpacing={1}
        columnSpacing={3}>
        <Grid
          item
          xs={6}>
          <Form.FC
            disableDefaultOverflow
            onSubmit={form.handleSubmit(processSubmit)}>
            <BaseWidget.FC title="Errors">
              <List>
                {fieldArray.fields.map((err, index) => (
                  <ListItem key={err.property}>
                    <ControlledTextField
                      formControl={form.control}
                      label={err.property}
                      fieldKey={`errors.${index}.newValue`} />
                  </ListItem>
                ))}
              </List>

              <Button
                variant="contained"
                type="submit">
                Save
              </Button>
            </BaseWidget.FC>
          </Form.FC>
        </Grid>

        <Grid
          item
          xs={6}>
          <Typography variant="h2">
            Validation errors
          </Typography>

          <List>
            {_.isEmpty(validationErrors) && (
              <ListItem>
                <ListItemIcon>
                  <Done color="success" />
                </ListItemIcon>

                <ListItemText
                  color="success"
                  secondary="Order is valid" />
              </ListItem>
            )}

            {_.map(validationErrors, (validationError, index) => (
              <ListItem key={index} >
                <ListItemIcon>
                  <Error color="error" />
                </ListItemIcon>

                <ListItemText
                  secondary={validationError.message}
                  color="error">
                </ListItemText>
              </ListItem>
            ))}

            {_.isEmpty(validationErrors) && (
              <ListItem>
                <Button
                  variant="contained"
                  color="success"
                  disabled={
                    !validateCachedOrderTuple.called ||
                    validateCachedOrderTuple.loading ||
                    overrideCachedOrderTuple.loading
                  }
                  onClick={handleClickOverrideOrderForProcessing}>
                  Override Order for Processing
                </Button>
              </ListItem>
            )}
          </List>
        </Grid>
      </Grid>
    </activityTemplates.AdminActivityTemplate.FC>
  );
};
